import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { RefreshCw, AlertTriangle } from 'lucide-react';

interface ResidentRisk {
  id: string;
  full_name: string;
  room_number: string | null;
  score: number;
  factors: string[];
  last_seen: string | null;
}

export function RiskDashboard() {
  const { user } = useAuth();
  const [residents, setResidents] = useState<ResidentRisk[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastCalculated, setLastCalculated] = useState<string>('');

  useEffect(() => { loadRiskData(); }, [user]);

  const loadRiskData = async () => {
    if (!user) return;
    if (!supabase) return;
    setLoading(true);
    try {
      const { data: profile, error: profileErr } = await supabase!
        .from('profiles').select('tenant_id').eq('id', user.id).single();
      if (profileErr) console.error('[RiskDashboard] profile query failed:', profileErr.message);
      if (!profile?.tenant_id) { setLoading(false); return; }
      const { data: resData } = await supabase!
        .from('residents').select('id, full_name, room_number')
        .eq('tenant_id', profile.tenant_id).eq('status', 'active');
      if (!resData) { setLoading(false); return; }
      const twoDaysAgo = new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString();
      const riskyResidents: ResidentRisk[] = await Promise.all(
        resData.map(async (r: any) => {
          const factors: string[] = [];
          let score = 0;
          const { count: noteCount } = await supabase!.from('care_notes')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', profile.tenant_id).eq('resident_id', r.id).gte('created_at', twoDaysAgo);
          if (!noteCount || noteCount === 0) { factors.push('No staff notes in 48 hours'); score += 30; }
          const { count: sosCount } = await supabase!.from('sos_events')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', profile.tenant_id).eq('user_id', r.id).gte('created_at', twoDaysAgo);
          if (sosCount && sosCount > 0) { factors.push(sosCount + ' SOS alert(s) recently'); score += 40; }
          const { count: remindersMissed } = await supabase!.from('reminders')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', profile.tenant_id).eq('user_id', r.id)
            .eq('completed', false).lt('scheduled_time', new Date().toISOString());
          if (remindersMissed && remindersMissed > 2) { factors.push(remindersMissed + ' missed reminders'); score += 20; }
          if (factors.length === 0) { factors.push('All checks normal'); }
          return { id: r.id, full_name: r.full_name || 'Unknown', room_number: r.room_number, score, factors, last_seen: null };
        })
      );
      riskyResidents.sort((a, b) => b.score - a.score);
      setResidents(riskyResidents);
      setLastCalculated(new Date().toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' }));
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const getRiskLevel = (score: number) => {
    if (score >= 60) return { label: 'Needs Attention', dotClass: 'bg-[rgb(var(--admin-danger))]' };
    if (score >= 30) return { label: 'Monitor', dotClass: 'bg-[rgb(var(--admin-warning))]' };
    return { label: 'Stable', dotClass: 'bg-[rgb(var(--admin-success))]' };
  };

  const highRisk = residents.filter(r => r.score >= 60);
  const medRisk = residents.filter(r => r.score >= 30 && r.score < 60);
  const lowRisk = residents.filter(r => r.score < 30);

    if (loading) return (<main className="p-6"><div className="flex items-center justify-center h-[60vh] text-[rgb(var(--admin-text-muted))]">Calculating risk scores...</div></main>);

  return (
      <main className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-[rgb(var(--admin-text-primary))]">Risk Monitor</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">Last calculated: {lastCalculated}</p>
        </div>
        <button onClick={loadRiskData} className="flex items-center gap-2 rounded-2xl px-4 py-2 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] text-sm text-[rgb(var(--admin-text-secondary))] hover:bg-[rgb(var(--admin-surface-2))] transition-all duration-200 active:scale-[0.98] cursor-pointer">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-3xl p-4 border border-[rgb(var(--admin-danger))] bg-[rgb(var(--admin-danger)/0.1)]">
          <div className="text-sm text-[rgb(var(--admin-text-secondary))]">High Risk</div>
          <div className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{highRisk.length}</div>
        </div>
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <div className="text-sm text-[rgb(var(--admin-text-secondary))]">Medium Risk</div>
          <div className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{medRisk.length}</div>
        </div>
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <div className="text-sm text-[rgb(var(--admin-text-secondary))]">Low Risk</div>
          <div className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{lowRisk.length}</div>
        </div>
      </div>
      {highRisk.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-[rgb(var(--admin-text-primary))]"><AlertTriangle size={18} className="inline mr-2 text-[rgb(var(--admin-danger))]" /> Needs Immediate Attention</h2>
          {highRisk.map(resident => (
            <div key={'alert-' + resident.id} className="rounded-2xl p-4 border-l-4 border-[rgb(var(--admin-danger))] bg-[rgb(var(--admin-danger)/0.05)] flex items-center justify-between">
              <div>
                <div className="text-[rgb(var(--admin-text-primary))] font-medium">{resident.full_name}</div>
                {resident.room_number && <div className="text-sm text-[rgb(var(--admin-text-muted))]">Room {resident.room_number}</div>}
                <div className="text-sm text-[rgb(var(--admin-text-muted))]">{resident.factors[0]}</div>
              </div>
              <button className="rounded-2xl px-4 py-2 bg-[rgb(var(--admin-accent))] text-white text-sm cursor-pointer hover:opacity-90 transition-all duration-200 active:scale-[0.98]">View</button>
            </div>
          ))}
        </div>
      )}
      {highRisk.length === 0 && residents.length > 0 && (<p className="text-sm text-[rgb(var(--admin-text-muted))]">No residents need immediate attention</p>)}
      {residents.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-[rgb(var(--admin-text-primary))]">All Residents</h2>
          {residents.map(resident => {
            const risk = getRiskLevel(resident.score);
            const isHigh = resident.score >= 60;
            return (
              <div key={resident.id} className={'rounded-2xl p-4 flex items-center justify-between transition-all duration-200 hover:bg-[rgb(var(--admin-surface-2))] active:scale-[0.98] ' + (isHigh ? 'border-l-4 border-[rgb(var(--admin-danger))] bg-[rgb(var(--admin-danger)/0.05)]' : 'bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))]')}>
                            <div className="flex items-center gap-3 w-[220px] shrink-0">
                  <div className={'w-3 h-3 rounded-full ' + risk.dotClass} />
                  <div>
                    <div className="text-[rgb(var(--admin-text-primary))] font-medium">{resident.full_name}</div>
                    {resident.room_number && <div className="text-sm text-[rgb(var(--admin-text-muted))]">Room {resident.room_number}</div>}
                  </div>
                </div>
                          <div className="flex-1 text-center text-sm text-[rgb(var(--admin-text-muted))]">{resident.factors[0]}</div>
                          <div className="text-sm text-[rgb(var(--admin-text-secondary))] w-[80px] text-right shrink-0">{risk.label}</div>
              </div>
            );
          })}
        </div>
      )}
      {residents.length === 0 && (
        <div className="rounded-2xl p-6 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] text-center">
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">No residents to monitor yet. Add residents to start tracking risk scores.</p>
        </div>
      )}
        </main>
  );
}